import { Content, isFilled } from "@prismicio/client";
import { SliceComponentProps } from "@prismicio/react";
import { appLocales, getPrismicLocale, Locales } from "@/lib/i18n";
import { getAllByIDs, getAllByUids } from "@/lib/prismic";
import { ProductDocument } from "@/prismicio-types";
import { getRelatedPrismicProductCategories } from "@/lib/prismic/products";
import BestSellers from "@/components/molecules/best-sellers";

export type ProductSliderProps = SliceComponentProps<Content.ProductSliderSlice> & {
  context?: { locale?: Locales };
};

const ProductSlider = async ({ slice, context }: ProductSliderProps) => {
  const locale = context?.locale || appLocales.defaultLocale;

  const { title, subtitle, products } = slice.primary;

  const productRefs = products.map((item) => item.product).filter(isFilled.contentRelationship);
  const ids = productRefs?.map((p) => p.id);

  let prismicProducts: ProductDocument[] | null = null;

  prismicProducts = (await getAllByIDs(ids, { lang: getPrismicLocale(locale) })) as ProductDocument[] | null;

  const productUIDs =
    prismicProducts
      ?.map((product) => product.alternate_languages.find((alt) => alt.lang === getPrismicLocale("fr"))?.uid)
      .filter((uid): uid is string => typeof uid === "string") || [];

  const frenchProducts = (await getAllByUids("product", productUIDs, "fr")) as ProductDocument[] | null;

  if (locale !== "fr") {
    prismicProducts = prismicProducts
      ?.map((localeProduct) => {
        const frenchEquivalent = frenchProducts?.find(
          (frenchProduct) =>
            frenchProduct.alternate_languages.find((alt) => alt.lang === getPrismicLocale(locale))?.uid ===
            localeProduct.uid
        );
        if (frenchEquivalent) {
          return {
            ...localeProduct,
            data: {
              ...localeProduct.data,
              images: frenchEquivalent.data.images,
              featuredImage: frenchEquivalent.data.featuredImage,
              productCollection: frenchEquivalent.data.productCollection,
              image_full_size_card: frenchEquivalent.data.image_full_size_card,
            },
          };
        }
        return undefined;
      })
      .filter(Boolean) as ProductDocument[];
  }

  if (!prismicProducts || !prismicProducts.length) return null;

  const productCategories = await getRelatedPrismicProductCategories(
    (locale === "fr" ? prismicProducts : frenchProducts) ?? [],
    locale
  );

  return (
    <>
      {productCategories && prismicProducts && (
        <BestSellers
          variant="default"
          headline={title!}
          productCategories={productCategories}
          description={subtitle!}
          products={prismicProducts}
          locale={locale}
        />
      )}
    </>
  );
};

export default ProductSlider;
